import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Center, useToast,
  Button, HStack, useColorModeValue, Badge, Text, Flex, Tag, Icon,
  InputGroup, InputLeftElement, Input, Alert, AlertIcon, AlertTitle, AlertDescription,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  VStack, SimpleGrid, Avatar, IconButton, Card, CardHeader, CardBody, CardFooter,
  Tooltip, Divider, Image
} from '@chakra-ui/react';
import { DeleteIcon, CheckIcon, EditIcon, SearchIcon, StarIcon, DownloadIcon, ViewIcon, AddIcon, LockIcon } from '@chakra-ui/icons';
import { FaUserCheck, FaTools, FaIdCard, FaCertificate, FaFileUpload } from 'react-icons/fa';
import { FiEye, FiAlertCircle, FiFilter, FiRefreshCw, FiUserCheck, FiUser, FiStar } from 'react-icons/fi';
import AdminPasswordResetModal from '../Common/AdminPasswordResetModal';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const WorkerList = () => {
  const [workers, setWorkers] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [certificationsLoading, setCertificationsLoading] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [certificationFile, setCertificationFile] = useState(null);
  const [certificationPreview, setCertificationPreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [workerToResetPassword, setWorkerToResetPassword] = useState(null);
  
  const toast = useToast();
  
  // Color scheme
  const tableBorderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const filterBg = useColorModeValue('white', 'gray.800');
  const filterBorderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch workers from API
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchWorkers();
  }, []);
  
  // Filter workers when data, search term, or status filter changes
  useEffect(() => {
    applyFilters();
  }, [workers, searchTerm, statusFilter]);

  // Function to apply filters
  const applyFilters = () => {
    let result = [...workers];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(worker => {
        // Check name
        if (worker.name && worker.name.toLowerCase().includes(searchLower)) return true;
        
        // Check email
        if (worker.email && worker.email.toLowerCase().includes(searchLower)) return true;
        
        // Check profession
        if (worker.profession && worker.profession.toLowerCase().includes(searchLower)) return true;
        
        // Check specialties
        if (worker.specialties && worker.specialties.some(specialty => 
          specialty.toLowerCase().includes(searchLower))) return true;
        
        return false;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      switch (statusFilter) {
        case 'verified':
          result = result.filter(worker => worker.verifiedAccount);
          break;
        case 'unverified':
          result = result.filter(worker => !worker.verifiedAccount);
          break;
        case 'premium':
          result = result.filter(worker => worker.premiumAccount);
          break;
        default:
          break;
      }
    }
    
    setFilteredWorkers(result);
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  // Function to preview certification
  const previewCertification = (fileUrl) => {
    setCertificationPreview(fileUrl);
  };

  // Fetch workers from API
  const fetchWorkers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/userview-worker/web/workers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch workers');
      }

      const data = await response.json();
      setWorkers(data);
      setFilteredWorkers(data);
    } catch (error) {
      setError(error.message);
      toast({
        title: 'Erro',
        description: 'Falha ao carregar trabalhadores',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (worker) => {
    // Open modal immediately with available data
    setSelectedWorker(worker);
    setIsModalOpen(true); // Open modal immediately
    setPhotoLoading(true); // Show loading spinner for photos
    setCertificationsLoading(true); // Show loading spinner for certifications
  
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
  
      // Fetch worker's photos asynchronously
      const fetchPhotos = fetch(`${API_BASE_URL}api/userview-worker/web/workers/${worker._id}/photo`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }).then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          const photoUrls = data.photos?.length > 0 ? data.photos.map(photo => `data:image/jpeg;base64,${photo}`) : [];
          setSelectedWorker((prevWorker) => ({ ...prevWorker, photoUrls }));
        } else {
          throw new Error('Failed to fetch worker photos');
        }
      }).catch((error) => {
        console.error('Error fetching photos:', error);
        toast({
          title: 'Erro',
          description: 'Falha ao carregar fotos do trabalhador',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }).finally(() => setPhotoLoading(false)); // Hide photo spinner
  
      // Fetch worker's certifications asynchronously
      const fetchCertifications = fetch(`${API_BASE_URL}api/userview-worker/web/workers/${worker._id}/certification`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }).then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          const certificationLinks = data.certifications?.length > 0 
            ? data.certifications.map(cert => ({
                name: cert.name,
                url: `data:application/pdf;base64,${cert.fileData}`, // Assuming PDF
              }))
            : [];
          setSelectedWorker((prevWorker) => ({ ...prevWorker, certificationLinks }));
        } else {
          throw new Error('Failed to fetch worker certifications');
        }
      }).catch((error) => {
        console.error('Error fetching certifications:', error);
        toast({
          title: 'Erro',
          description: 'Falha ao carregar certificações do trabalhador',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }).finally(() => setCertificationsLoading(false)); // Hide certifications spinner
  
      // Wait for both photo and certification fetches to complete
      await Promise.all([fetchPhotos, fetchCertifications]);
  
    } catch (error) {
      console.error('Error during modal data loading:', error);
    }
  };

  // Function to handle password reset
  const handlePasswordReset = (worker) => {
    if (!worker || !worker._id) {
      console.error('Invalid worker data for password reset');
      toast({
        title: 'Erro',
        description: 'Dados do trabalhador inválidos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setWorkerToResetPassword(worker);
    setIsPasswordResetOpen(true);
  };

  // Function to delete worker
  const handleDelete = async (workerId) => {
    if (window.confirm('Tem certeza que deseja excluir este trabalhador?')) {
      try {
        const token = localStorage.getItem('userToken_TOVIAPPS');
        const response = await fetch(`${API_BASE_URL}api/userview-worker/web/workers/${workerId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          setWorkers(workers.filter((worker) => worker._id !== workerId));
          toast({
            title: 'Sucesso',
            description: 'Trabalhador excluído com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        } else {
          throw new Error('Falha ao excluir trabalhador');
        }
      } catch (error) {
        toast({
          title: 'Erro',
          description: 'Falha ao excluir trabalhador',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Function to verify worker account
  const handleVerifyAccount = async (workerId) => {
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/userview-worker/web/workers/verify/${workerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const updatedWorkers = workers.map((worker) =>
          worker._id === workerId ? { ...worker, verifiedAccount: true } : worker
        );
        setWorkers(updatedWorkers);
        
        // Also update selectedWorker if it's the one being updated
        if (selectedWorker && selectedWorker._id === workerId) {
          setSelectedWorker({
            ...selectedWorker,
            verifiedAccount: true
          });
        }
        
        toast({
          title: 'Sucesso',
          description: 'Trabalhador verificado com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('Falha ao verificar trabalhador');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao verificar trabalhador',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Function to display categories as tags
  const displayCategories = (categories) => {
    return categories.map((category) => (
      <Tag key={category._id} colorScheme="teal" mr={1} mb={1}>
        {category.name}
      </Tag>
    ));
  };

  // Function to display subcategories as tags
  const displaySubCategories = (subCategories) => {
    return subCategories.map((subCategory) => (
      <Tag key={subCategory._id} colorScheme="blue" mr={1} mb={1}>
        {subCategory.name}
      </Tag>
    ));
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPhotoFile(file);
    }
  };
  
  // Handle certification file change
  const handleCertificationChange = (event) => {
    const file = event.target.files[0];
    if (file) {
    setCertificationFile(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile || !selectedWorker) return;
  
    const formData = new FormData();
    formData.append('photo', photoFile);
  
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/userview-worker/web/workers/${selectedWorker._id}/photo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
  
      if (response.ok) {
        const updatedWorker = await response.json();
        toast({
          title: 'Sucesso',
          description: 'Foto carregada com sucesso!',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setWorkers(workers.map((w) => (w._id === updatedWorker._id ? updatedWorker : w)));
      } else {
        throw new Error('Falha ao carregar foto');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar foto',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Upload certification function
  const uploadCertification = async () => {
    if (!certificationFile || !selectedWorker) return;

    const formData = new FormData();
    formData.append('certification', certificationFile);

    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/userview-worker/web/workers/${selectedWorker._id}/certification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const updatedWorker = await response.json();
        toast({
          title: 'Sucesso',
          description: 'Certificação carregada com sucesso!',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setWorkers(workers.map((w) => (w._id === updatedWorker._id ? updatedWorker : w)));
      } else {
        throw new Error('Falha ao carregar certificação');
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar certificação',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  // Count workers by status
  const getCounts = () => {
    const verified = workers.filter(worker => worker.verifiedAccount).length;
    const unverified = workers.filter(worker => !worker.verifiedAccount).length;
    const premium = workers.filter(worker => worker.premiumAccount).length;
    
    return { verified, unverified, premium, total: workers.length };
  };
  
  const counts = getCounts();

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">Lista de Prestadores de Serviço</Heading>
        <Tag colorScheme="blue" size="lg" borderRadius="full">
          <Flex align="center">
            <Icon as={FiUser} mr={2} />
            <Text>{workers.length} Prestadores</Text>
          </Flex>
        </Tag>
      </Flex>

      {/* Status Summary */}
      <Flex 
        mb={4} 
        gap={3} 
        flexWrap="wrap"
        bg={filterBg}
        p={3}
        borderRadius="md"
        borderWidth="1px"
        borderColor={filterBorderColor}
      >
        <Button 
          leftIcon={<CheckIcon />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'verified' ? 'all' : 'verified')}
        >
          Verificados ({counts.verified})
        </Button>
        
        <Button 
          leftIcon={<FiAlertCircle />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'unverified' ? 'all' : 'unverified')}
        >
          Não Verificados ({counts.unverified})
        </Button>
        
        <Button 
          leftIcon={<StarIcon />} 
          bg="#4bedffff" 
          color="black" 
          size="sm" 
          variant="solid"
          onClick={() => setStatusFilter(statusFilter === 'premium' ? 'all' : 'premium')}
        >
          Premium ({counts.premium})
        </Button>
        
        <Button 
          leftIcon={<FiRefreshCw />} 
          colorScheme="blue" 
          size="sm" 
          variant="ghost"
          onClick={resetFilters}
          ml="auto"
        >
          Limpar Filtros
        </Button>
      </Flex>

      {/* Search Box */}
      <Flex mb={4}>
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input 
            placeholder="Buscar por nome, email, profissão..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="md"
          />
        </InputGroup>
      </Flex>

      {loading && !selectedWorker ? (
        <Center h="50vh">
          <Spinner size="xl" thickness="4px" color="blue.500" />
        </Center>
      ) : error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle mr={2}>Erro ao carregar dados</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
          <Button ml="auto" colorScheme="blue" size="sm" onClick={fetchWorkers}>
            Tentar novamente
          </Button>
        </Alert>
      ) : (
        <Box 
          overflowX="auto"
          borderWidth="1px"
          borderRadius="lg"
          borderColor={tableBorderColor}
        >
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>Nome</Th>
                <Th>Email</Th>
                <Th>Profissão</Th>
                <Th>Status</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredWorkers.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={6}>
                    <Text color="gray.500">
                      {workers.length > 0 
                        ? 'Nenhum prestador encontrado com os filtros aplicados' 
                        : 'Nenhum prestador de serviço encontrado'}
                    </Text>
                  </Td>
                </Tr>
              ) : (
                filteredWorkers.map((worker) => (
                  <Tr 
                    key={worker._id} 
                    _hover={{ bg: hoverBg }}
                    transition="background 0.2s"
                  >
                    <Td>
                      <Flex align="center">
                        <Avatar 
                          size="sm" 
                          name={worker.name} 
                          src={worker.profilePhoto} 
                          mr={3} 
                          bg="blue.500"
                        />
                        <Button
                          variant="link"
                          colorScheme="yellow"
                          onClick={() => openModal(worker)}
                        >
                          {worker.name}
                        </Button>
                      </Flex>
                    </Td>
                    <Td>{worker.email}</Td>
                    <Td>
                      <Tag colorScheme="purple" variant="subtle" size="sm">
                        {worker.profession}
                      </Tag>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={worker.verifiedAccount ? 'yellow' : 'red'} 
                        px={2} py={1} borderRadius="full"
                      >
                        <Flex align="center">
                          <Icon 
                            as={worker.verifiedAccount ? CheckIcon : FiAlertCircle} 
                            mr={1} 
                            boxSize="10px" 
                          />
                          <Text>
                            {worker.verifiedAccount ? 'Verificado' : 'Não Verificado'}
                          </Text>
                        </Flex>
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          leftIcon={<FiEye />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => openModal(worker)}
                        >
                          Detalhes
                        </Button>
                        <Button
                          leftIcon={<FiUserCheck />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => handleVerifyAccount(worker._id)}
                          isDisabled={worker.verifiedAccount}
                        >
                          Verificar
                        </Button>
                        <Button
                          leftIcon={<LockIcon />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => handlePasswordReset(worker)}
                        >
                          Redefinir Senha
                        </Button>
                        <Button
                          leftIcon={<DeleteIcon />}
                          bg="#4bedffff"
                          color="black"
                          size="sm"
                          variant="solid"
                          onClick={() => handleDelete(worker._id)}
                        >
                          Excluir
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Worker Details Modal */}
      {selectedWorker && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
          <ModalOverlay backdropFilter="blur(2px)" />
          <ModalContent borderRadius="lg">
            <ModalHeader bg="blue.50" borderTopRadius="lg" display="flex" alignItems="center">
              <Avatar 
                size="md" 
                name={selectedWorker.name} 
                src={selectedWorker.profilePhoto} 
                mr={4} 
                bg="blue.500"
              />
              <VStack align="start" spacing={0}>
                <Heading size="md">{selectedWorker.name}</Heading>
                <Text fontSize="sm" color="gray.600">{selectedWorker.profession}</Text>
              </VStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pt={6}>
              <SimpleGrid columns={[1, null, 2]} spacing={6}>
                {/* Left Column - Personal Information */}
                <Card variant="outline" p={4}>
                  <CardHeader pb={2}>
                    <Heading size="sm" color="blue.600">Informações Pessoais</Heading>
                  </CardHeader>
                  <Divider mb={3} />
                  <CardBody>
                    <VStack align="start" spacing={3}>
                      <Flex align="center">
                        <Badge colorScheme="blue" mr={2}><FaIdCard /></Badge>
                        <Text><strong>Email:</strong> {selectedWorker.email}</Text>
                      </Flex>
                      <Flex align="center">
                        <Badge colorScheme="green" mr={2}><FaTools /></Badge>
                        <Text><strong>Telefone:</strong> {selectedWorker.phone || 'Não informado'}</Text>
                      </Flex>
                      <Flex align="center">
                        <Badge colorScheme="purple" mr={2}><FaTools /></Badge>
                        <Text><strong>Profissão:</strong> {selectedWorker.profession}</Text>
                      </Flex>
                      <Text><strong>Endereço:</strong> {selectedWorker.address}</Text>
                      <Text><strong>Histórico de Trabalho:</strong> {selectedWorker.workHistory}</Text>
                      <Flex align="center" wrap="wrap">
                        <Badge colorScheme={selectedWorker.verifiedAccount ? 'yellow' : 'red'} p={2} borderRadius="md" mr={2} mb={2}>
                          <Flex align="center">
                            {selectedWorker.verifiedAccount ? <CheckIcon mr={1} /> : null}
                            <Text><strong>Conta:</strong> {selectedWorker.verifiedAccount ? 'Verificada' : 'Não Verificada'}</Text>
                          </Flex>
                        </Badge>
                        <Badge colorScheme={selectedWorker.premiumAccount ? 'yellow' : 'gray'} p={2} borderRadius="md">
                          <Flex align="center">
                            {selectedWorker.premiumAccount ? <StarIcon mr={1} /> : null}
                            <Text><strong>Premium:</strong> {selectedWorker.premiumAccount ? 'Sim' : 'Não'}</Text>
                          </Flex>
                        </Badge>
                      </Flex>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Right Column - Skills and Categories */}
                <Card variant="outline" p={4}>
                  <CardHeader pb={2}>
                    <Heading size="sm" color="blue.600">Especialidades e Categorias</Heading>
                  </CardHeader>
                  <Divider mb={3} />
                  <CardBody>
                    <VStack align="start" spacing={4}>
                      <Box w="100%">
                        <Text fontWeight="bold" mb={2}>Especialidades:</Text>
                        <Flex wrap="wrap">
                          {selectedWorker.specialties && selectedWorker.specialties.length > 0 ? (
                            selectedWorker.specialties.map((specialty, index) => (
                              <Tag key={index} colorScheme="green" m={1} borderRadius="full">
                                {specialty}
                              </Tag>
                            ))
                          ) : (
                            <Text fontSize="sm" color="gray.500">Nenhuma especialidade cadastrada</Text>
                          )}
                        </Flex>
                      </Box>

                      <Box w="100%">
                        <Text fontWeight="bold" mb={2}>Categorias:</Text>
                        <Flex wrap="wrap">
                          {displayCategories(selectedWorker.categories)}
                        </Flex>
                      </Box>

                      <Box w="100%">
                        <Text fontWeight="bold" mb={2}>Subcategorias:</Text>
                        <Flex wrap="wrap">
                          {displaySubCategories(selectedWorker.subCategories)}
                        </Flex>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>
              </SimpleGrid>

              <SimpleGrid columns={[1, null, 2]} spacing={6} mt={6}>
                {/* Photos Section */}
                <Card variant="outline" p={4}>
                  <CardHeader pb={2}>
                    <Heading size="sm" color="blue.600">Fotos</Heading>
                  </CardHeader>
                  <Divider mb={3} />
                  <CardBody>
                    {photoLoading ? (
                      <Center py={4}>
                        <Spinner size="md" color="blue.500" />
                      </Center>
                    ) : (
                      <>
                        <SimpleGrid columns={[2, 3, 4]} spacing={3} mb={4}>
                          {selectedWorker.photoUrls && selectedWorker.photoUrls.length > 0 ? (
                            selectedWorker.photoUrls.map((photoUrl, index) => (
                              <Box key={index} position="relative">
                                <Image
                                  src={photoUrl}
                                  alt={`Foto ${index + 1}`}
                                  borderRadius="md"
                                  objectFit="cover"
                                  height="80px"
                                  width="100%"
                                />
                              </Box>
                            ))
                          ) : (
                            <Text color="gray.500">Nenhuma foto disponível</Text>
                          )}
                        </SimpleGrid>

                        <Flex direction="column" mt={4}>
                          <Text fontWeight="bold" mb={2} fontSize="sm">Adicionar Nova Foto:</Text>
                          <InputGroup size="sm">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoChange}
                              p={1}
                              bg="gray.50"
                              borderRadius="md"
                              mb={2}
                            />
                          </InputGroup>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            leftIcon={<FaFileUpload />}
                            onClick={uploadPhoto}
                            isDisabled={!photoFile}
                          >
                            Upload Foto
                          </Button>
                        </Flex>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Certifications Section */}
                <Card variant="outline" p={4}>
                  <CardHeader pb={2}>
                    <Heading size="sm" color="blue.600">Certificações</Heading>
                  </CardHeader>
                  <Divider mb={3} />
                  <CardBody>
                    {certificationsLoading ? (
                      <Center py={4}>
                        <Spinner size="md" color="blue.500" />
                      </Center>
                    ) : (
                      <>
                        <VStack align="stretch" spacing={3} mb={4}>
                          {selectedWorker.certificationLinks && selectedWorker.certificationLinks.length > 0 ? (
                            selectedWorker.certificationLinks.map((cert, index) => (
                              <Flex key={index} justify="space-between" align="center" p={2} bg="gray.50" borderRadius="md">
                                <Flex align="center">
                                  <FaCertificate color="blue" />
                                  <Text ml={2} fontWeight="medium">{cert.name}</Text>
                                </Flex>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  leftIcon={<ViewIcon />}
                                  onClick={() => previewCertification(cert.url)}
                                >
                                  Visualizar
                                </Button>
                              </Flex>
                            ))
                          ) : (
                            <Text color="gray.500">Nenhuma certificação disponível</Text>
                          )}
                        </VStack>

                        <Flex direction="column" mt={4}>
                          <Text fontWeight="bold" mb={2} fontSize="sm">Adicionar Nova Certificação:</Text>
                          <InputGroup size="sm">
                            <Input
                              type="file"
                              accept=".pdf,.docx,.jpg,.png"
                              onChange={handleCertificationChange}
                              p={1}
                              bg="gray.50"
                              borderRadius="md"
                              mb={2}
                            />
                          </InputGroup>
                          <Button
                            size="sm"
                            colorScheme="blue"
                            leftIcon={<FaFileUpload />}
                            onClick={uploadCertification}
                            isDisabled={!certificationFile}
                          >
                            Upload Certificação
                          </Button>
                        </Flex>
                      </>
                    )}
                  </CardBody>
                </Card>
              </SimpleGrid>
            </ModalBody>
            <ModalFooter borderTop="1px" borderColor="gray.100" mt={4} p={4}>
              <Button colorScheme="gray" mr={3} onClick={() => setIsModalOpen(false)}>
                Fechar
              </Button>
              <Button
                bg="#4bedffff"
                color="black"
                onClick={() => handleVerifyAccount(selectedWorker._id)}
                isDisabled={selectedWorker.verifiedAccount}
                leftIcon={<FiUserCheck />}
              >
                {selectedWorker.verifiedAccount ? 'Verificado' : 'Verificar Conta'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>        
      )}

      {/* Certification Preview Modal */}
      {certificationPreview && (
        <Modal isOpen={!!certificationPreview} onClose={() => setCertificationPreview(null)} size="xl">
          <ModalOverlay backdropFilter="blur(2px)" />
          <ModalContent borderRadius="lg">
            <ModalHeader bg="blue.50" borderTopRadius="lg" display="flex" alignItems="center">
              <FaCertificate color="blue" size="24px" style={{ marginRight: '12px' }} />
              <Text>Visualização da Certificação</Text>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody p={0}>
              <Box height="600px" borderWidth="1px" borderRadius="md" overflow="hidden">
                <iframe
                  src={certificationPreview}
                  title="Certification Preview"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" leftIcon={<DownloadIcon />} mr={3}>
                Download
              </Button>
              <Button colorScheme="gray" onClick={() => setCertificationPreview(null)}>
                Fechar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Password Reset Modal */}
      {workerToResetPassword && workerToResetPassword._id && (
        <AdminPasswordResetModal
          isOpen={isPasswordResetOpen}
          onClose={() => {
            setIsPasswordResetOpen(false);
            setWorkerToResetPassword(null);
          }}
          userType="worker"
          userId={workerToResetPassword._id}
          userName={workerToResetPassword.name || 'Trabalhador'}
          hasFastPassword={true}
          onSuccess={() => {
            toast({
              title: 'Sucesso',
              description: 'Senha redefinida com sucesso',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            // Don't refetch workers here as it might cause the list to flicker
          }}
        />
      )}
    </Box>
  );
};

export default WorkerList;